import {
	ASTEROID_RAM_DAMAGE,
	FUEL_MAX,
	LASER_DAMAGE,
	MISSILE_DAMAGE,
	SHIP_STRENGTH,
} from "../constants";
import {
	type Destroyable,
	type Entity,
	isDestroyable,
	isMovable,
	isProjectile,
	type Laser,
	type Pickup,
	type Planet,
	type Ship,
} from "../entities";
import type { SimEvent } from "../events";
import { powerUpDuration } from "../match-rules";
import * as DMath from "../math/det-math";
import { nextFloat } from "../math/rng";
import { wrappedAxisDelta } from "../math/torus";
import type { WorldState } from "../state";
import { findEntity, isQueuedForRemoval, queueRemoval } from "../world";
import { destroyEntity } from "./destruction";

/**
 * legacy: CollisionStrategy.cpp. Sweep-and-prune on the x axis, circle-vs-circle narrow
 * phase, then the typed rule matrix. Each overlapping pair is handled in both directions
 * (active/passive), except ship-ship which is handled once. Objects queued for removal
 * this tick still collide (as in the original). Classic ignores the world seam; with
 * `rules.wrapPhysics` objects touching across the seam collide too.
 */
export function applyCollisions(world: WorldState, events: SimEvent[]): void {
	for (const e of world.entities) if (e.kind === "spawnPoint") e.occupied = false;

	const destroyed = new Set<number>();
	const wrap = world.rules.wrapPhysics;
	const { height } = world.size;
	const proxies = broadphaseProxies(world, wrap);
	const handled = wrap ? new Set<string>() : null;
	let active: Proxy[] = [];

	for (const curr of proxies) {
		// Drop objects that end before this one starts; the rest overlap it on the x axis.
		active = active.filter((o) => o.right >= curr.left);
		for (const other of active) {
			const a = curr.e;
			const b = other.e;
			if (a === b) continue;
			const dx = curr.x - other.x;
			const dy = wrap ? wrappedAxisDelta(b.pos.y, a.pos.y, height) : a.pos.y - b.pos.y;
			const r = a.radius + b.radius;
			if (dx * dx + dy * dy >= r * r) continue;
			if (handled) {
				const key = a.id < b.id ? `${a.id}:${b.id}` : `${b.id}:${a.id}`;
				if (handled.has(key)) continue;
				handled.add(key);
			}
			handle(world, b, a, events, destroyed);
			if (!(a.kind === "ship" && b.kind === "ship")) handle(world, a, b, events, destroyed);
		}
		active.push(curr);
	}
}

interface Proxy {
	e: Entity;
	/** x used for the sweep (shifted by -width for seam ghosts). */
	x: number;
	left: number;
	right: number;
}

/** Proxies sorted by left edge; in wrap mode objects near the right edge get a ghost at x - width. */
function broadphaseProxies(world: WorldState, wrap: boolean): Proxy[] {
	const proxies: Proxy[] = [];
	const add = (e: Entity, x: number) =>
		proxies.push({ e, x, left: x - e.radius, right: x + e.radius });
	let maxRadius = 0;
	for (const e of world.entities) {
		add(e, e.pos.x);
		maxRadius = Math.max(maxRadius, e.radius);
	}
	if (wrap) {
		const { width } = world.size;
		// Anything that can touch the left edge from across the seam (plus a tick of motion).
		const margin = 2 * maxRadius + SEAM_SLACK;
		const count = proxies.length;
		for (let i = 0; i < count; i++) {
			const p = proxies[i];
			if (p && p.x > width - margin) add(p.e, p.x - width);
		}
	}
	return proxies.sort((a, b) => a.left - b.left || a.e.id - b.e.id);
}

/** Objects may sit slightly past the seam during the collision pass (wrap runs after it). */
const SEAM_SLACK = 64;

export function overlaps(a: Entity, b: Entity): boolean {
	const r = a.radius + b.radius;
	const dx = a.pos.x - b.pos.x;
	const dy = a.pos.y - b.pos.y;
	return dx * dx + dy * dy < r * r;
}

/** Applies every rule whose (active, passive) types match; several may apply (legacy did not use else-if). */
function handle(
	world: WorldState,
	active: Entity,
	passive: Entity,
	events: SimEvent[],
	destroyed: Set<number>,
): void {
	// Projectile hits something destroyable: the projectile is consumed.
	if (isProjectile(active) && isDestroyable(passive)) queueRemoval(world, active.id);

	// Destroyable crashing into a planet: destroyed, unless a shield bounces the ship off.
	if (isDestroyable(active) && passive.kind === "planet") {
		if (active.kind === "ship" && absorb(active, "all", events, destroyed)) {
			bounceOffPlanet(world, active, passive);
		} else {
			destroyEntity(world, active, passive, events, destroyed);
		}
	}

	// Destroyable hit by a projectile: destroyed (classic) or damaged (hull-damage rule),
	// after any shield has had its say.
	if (isDestroyable(active) && isProjectile(passive)) {
		const shieldCost = passive.kind === "laser" ? 1 : "all";
		if (!(active.kind === "ship" && absorb(active, shieldCost, events, destroyed))) {
			const damage = passive.kind === "laser" ? LASER_DAMAGE : MISSILE_DAMAGE;
			hit(world, active, passive, damage, events, destroyed);
		}
	}

	if (active.kind === "asteroid" && passive.kind === "asteroid") {
		destroyEntity(world, active, passive, events, destroyed);
	}

	if (active.kind === "ship" && passive.kind === "ship") bounceShips(active, passive, events);

	if (active.kind === "ship" && passive.kind === "asteroid") {
		if (absorb(active, "all", events, destroyed)) {
			destroyEntity(world, passive, active, events, destroyed); // the shield smashes it
		} else {
			hit(world, active, passive, ASTEROID_RAM_DAMAGE, events, destroyed);
		}
	}

	// Asteroid rammed by a ship: shoved (classic) or smashed (hull-damage rule).
	if (active.kind === "asteroid" && passive.kind === "ship") {
		if (world.rules.damage) {
			destroyEntity(world, active, passive, events, destroyed);
		} else {
			active.vel.x += passive.vel.x;
			active.vel.y += passive.vel.y;
		}
	}

	// Lasers vanish on planets (legacy steps them back one tick first), or ricochet.
	if (active.kind === "laser" && passive.kind === "planet") {
		if (world.rules.laserBounce) {
			bounceLaser(world, active, passive);
		} else {
			active.pos.x -= active.vel.x;
			active.pos.y -= active.vel.y;
			queueRemoval(world, active.id);
		}
	}

	if (active.kind === "spawnPoint" && isMovable(passive)) active.occupied = true;

	if (active.kind === "ship" && passive.kind === "pickup") {
		collectPickup(world, active, passive, events);
	}

	// A comet smashes any pod in its way.
	if (active.kind === "pickup" && passive.kind === "asteroid") {
		smashPickup(world, active, passive, events);
	}
}

/** An asteroid destroys a pod (unless a ship collected it first this tick). */
function smashPickup(
	world: WorldState,
	pickup: Pickup,
	asteroid: Entity,
	events: SimEvent[],
): void {
	if (isQueuedForRemoval(world, pickup.id)) return;
	queueRemoval(world, pickup.id);
	events.push({
		type: "destroyed",
		id: pickup.id,
		kind: "pickup",
		pos: { x: pickup.pos.x, y: pickup.pos.y },
		angle: pickup.angle,
		cause: { id: asteroid.id, kind: asteroid.kind, pos: { x: asteroid.pos.x, y: asteroid.pos.y } },
		creditedPlayer: null,
	});
}

/** legacy: both ships step back one tick, then each keeps -half its own + half the other's velocity. */
function bounceShips(a: Ship, b: Ship, events: SimEvent[]): void {
	a.pos.x -= a.vel.x;
	a.pos.y -= a.vel.y;
	b.pos.x -= b.vel.x;
	b.pos.y -= b.vel.y;

	const ax = a.vel.x;
	const ay = a.vel.y;
	a.vel.x = -ax / 2 + b.vel.x / 2;
	a.vel.y = -ay / 2 + b.vel.y / 2;
	b.vel.x = -b.vel.x / 2 + ax / 2;
	b.vel.y = -b.vel.y / 2 + ay / 2;

	events.push({
		type: "boing",
		a: a.id,
		b: b.id,
		pos: { x: (a.pos.x + b.pos.x) / 2, y: (a.pos.y + b.pos.y) / 2 },
	});
}

/**
 * Shield power-up: absorbs a hit costing `cost` laser hits ("all" for missiles, asteroids
 * and planets). A hit bigger than what is left still only breaks the shield.
 */
function absorb(
	ship: Ship,
	cost: number | "all",
	events: SimEvent[],
	destroyed: Set<number>,
): boolean {
	if (ship.shield <= 0 || destroyed.has(ship.id)) return false;
	ship.shield = cost === "all" ? 0 : Math.max(0, ship.shield - cost);
	events.push({
		type: "shieldHit",
		id: ship.id,
		shieldLeft: ship.shield,
		pos: { x: ship.pos.x, y: ship.pos.y },
	});
	return true;
}

/** Shield restitution for a planet crash: the ship keeps half its speed into the surface. */
const SHIELD_BOUNCE = 0.5;

/** A shielded ship bounces off the planet's surface (wrap-aware normal) and is put outside it. */
function bounceOffPlanet(world: WorldState, ship: Ship, planet: Planet): void {
	const { width, height } = world.size;
	const wrap = world.rules.wrapPhysics;
	let nx = wrap ? wrappedAxisDelta(planet.pos.x, ship.pos.x, width) : ship.pos.x - planet.pos.x;
	let ny = wrap ? wrappedAxisDelta(planet.pos.y, ship.pos.y, height) : ship.pos.y - planet.pos.y;
	const len = DMath.hypot(nx, ny);
	if (len === 0) {
		nx = 1;
		ny = 0;
	} else {
		nx /= len;
		ny /= len;
	}
	const dot = ship.vel.x * nx + ship.vel.y * ny;
	if (dot < 0) {
		ship.vel.x -= (1 + SHIELD_BOUNCE) * dot * nx;
		ship.vel.y -= (1 + SHIELD_BOUNCE) * dot * ny;
	}
	const surface = planet.radius + ship.radius + 1;
	ship.pos.x = planet.pos.x + nx * surface;
	ship.pos.y = planet.pos.y + ny * surface;
}

/** A ship flies through a pod and its effect starts at once (the first ship wins a tie). */
function collectPickup(world: WorldState, ship: Ship, pickup: Pickup, events: SimEvent[]): void {
	if (isQueuedForRemoval(world, pickup.id)) return;
	const { rules } = world;
	switch (pickup.item) {
		case "fuel":
			ship.fuel = Math.min(FUEL_MAX, ship.fuel + rules.fuelRefill);
			break;
		case "extraMissiles":
			ship.missiles += rules.extraMissiles;
			break;
		case "tripleLaser":
			ship.tripleLaserTicks = powerUpDuration(rules, "tripleLaser");
			break;
		case "warp":
			ship.warpTicks = powerUpDuration(rules, "warp");
			break;
		case "shield":
			ship.shield = rules.shieldHits;
			break;
		case "homing":
			ship.homingShots += rules.homingShots;
			ship.missiles += rules.homingShots;
			break;
		case "freeze":
			freezeOpponent(world, ship, events);
			break;
		case "repair":
			ship.hull = Math.min(SHIP_STRENGTH, ship.hull + rules.repairHull);
			break;
	}
	queueRemoval(world, pickup.id);
	events.push({
		type: "pickedUp",
		id: pickup.id,
		player: ship.player,
		item: pickup.item,
		pos: { x: pickup.pos.x, y: pickup.pos.y },
	});
}

/** Freeze power-up: a random opponent with a ship in the world loses control for a while. */
function freezeOpponent(world: WorldState, collector: Ship, events: SimEvent[]): void {
	const victims: Ship[] = [];
	for (const p of world.players) {
		if (p.index === collector.player || p.shipId === null) continue;
		const e = findEntity(world, p.shipId);
		if (e?.kind === "ship" && !isQueuedForRemoval(world, e.id)) victims.push(e);
	}
	if (victims.length === 0) return;
	const victim = victims[Math.floor(nextFloat(world.rng) * victims.length)];
	if (!victim) return;
	victim.frozenTicks = world.rules.freezeTicks;
	events.push({
		type: "frozen",
		id: victim.id,
		player: victim.player,
		by: collector.player,
		ticks: victim.frozenTicks,
	});
}

/**
 * Classic: any hit destroys (legacy one-hit kills). Hull-damage rule: subtract `damage`
 * from the hull and destroy only when it runs out; otherwise emit `damaged` (hit flash).
 */
function hit(
	world: WorldState,
	target: Destroyable,
	cause: Entity,
	damage: number,
	events: SimEvent[],
	destroyed: Set<number>,
): void {
	if (!world.rules.damage) {
		destroyEntity(world, target, cause, events, destroyed);
		return;
	}
	if (destroyed.has(target.id)) return;
	target.hull -= damage;
	if (target.hull <= 1e-9) {
		destroyEntity(world, target, cause, events, destroyed);
		return;
	}
	events.push({
		type: "damaged",
		id: target.id,
		kind: target.kind,
		hull: target.hull,
		pos: { x: target.pos.x, y: target.pos.y },
	});
}

/**
 * Laser-bounce rule: reflect the laser off the planet's surface normal
 * (v' = v - 2(v·n)n) and place it just outside the surface. The laser keeps its lifetime.
 */
function bounceLaser(world: WorldState, laser: Laser, planet: Planet): void {
	const { width, height } = world.size;
	const wrap = world.rules.wrapPhysics;
	let nx = wrap ? wrappedAxisDelta(planet.pos.x, laser.pos.x, width) : laser.pos.x - planet.pos.x;
	let ny = wrap ? wrappedAxisDelta(planet.pos.y, laser.pos.y, height) : laser.pos.y - planet.pos.y;
	const len = DMath.hypot(nx, ny);
	if (len === 0) {
		queueRemoval(world, laser.id); // exactly at the centre: nothing sensible to reflect off
		return;
	}
	nx /= len;
	ny /= len;

	const dot = laser.vel.x * nx + laser.vel.y * ny;
	if (dot < 0) {
		laser.vel.x -= 2 * dot * nx;
		laser.vel.y -= 2 * dot * ny;
		laser.angle = DMath.atan2(laser.vel.y, laser.vel.x);
	}
	const surface = planet.radius + laser.radius + 0.5;
	laser.pos.x = planet.pos.x + nx * surface;
	laser.pos.y = planet.pos.y + ny * surface;
}
